// src/follower/components/MacroBanner.jsx
// Displays macro pause state from followerMacroEvent.
// Three states: hard_pause (amber), soft_caution (subtle yellow), none (null).

import React from "react";

const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    yellow: "#ffe066",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

// Find the matching HighImpactEvent for the active hard_pause to extract its window times
function findPauseEvent(today_high_impact) {
    if (!Array.isArray(today_high_impact)) return null;
    return today_high_impact.find(e => e.pause_type === "hard_pause") ?? null;
}

function findCautionEvent(today_high_impact) {
    if (!Array.isArray(today_high_impact)) return null;
    return today_high_impact.find(e => e.pause_type === "soft_caution" && !e.has_actual) ?? null;
}

// ── Hard pause banner (FOMC / high-impact) ────────────────────────────────────
function HardPauseBanner({ macro }) {
    const pauseEvent = findPauseEvent(macro.today_high_impact);

    return (
        <div style={{
            background:   C.amber + "14",
            border:       `1px solid ${C.amber}55`,
            borderLeft:   `3px solid ${C.amber}`,
            borderRadius: 8,
            padding:      "14px 20px",
            display:      "flex",
            alignItems:   "flex-start",
            gap:          14,
        }}>
            {/* Icon */}
            <div style={{
                width:      32,
                height:     32,
                borderRadius: "50%",
                background: C.amber + "20",
                border:     `1px solid ${C.amber}40`,
                display:    "grid",
                placeItems: "center",
                flexShrink: 0,
                marginTop:  1,
            }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1.5L12.5 11.5H1.5L7 1.5Z" stroke={C.amber} strokeWidth="1.2" strokeLinejoin="round"/>
                    <line x1="7" y1="5.5" x2="7" y2="8.5" stroke={C.amber} strokeWidth="1.2" strokeLinecap="round"/>
                    <circle cx="7" cy="10" r=".7" fill={C.amber}/>
                </svg>
            </div>

            {/* Text block */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontFamily:    FONT,
                    fontSize:      11,
                    fontWeight:    500,
                    color:         C.amber,
                    letterSpacing: ".6px",
                    marginBottom:  5,
                    textTransform: "uppercase",
                }}>
                    {macro.pause_reason || "Trading Paused — Macro Event"}
                </div>
                {pauseEvent?.pause_window && (
                    <div style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    color:         C.amber + "bb",
                    letterSpacing: ".3px",
                    display:       "flex",
                    alignItems:    "center",
                    gap:           6,
                    }}>
                    {/* Clock icon */}
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="4" stroke={C.amber} strokeWidth="1" opacity=".7"/>
                    <line x1="5" y1="2.5" x2="5" y2="5" stroke={C.amber} strokeWidth="1" strokeLinecap="round" opacity=".7"/>
                    <line x1="5" y1="5" x2="7" y2="6.2" stroke={C.amber} strokeWidth="1" strokeLinecap="round" opacity=".7"/>
                    </svg>
                    Pause window: {pauseEvent.pause_window} ET
                    </div>
                    )}
                <div style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    color:         C.meta,
                    letterSpacing: ".3px",
                    marginTop:     4,
                }}>
                    New positions are blocked. Open positions remain visible and monitored.
                </div>
            </div>

            {/* Pulse dot */}
            <div style={{ flexShrink: 0, marginTop: 8, position: "relative", width: 8, height: 8 }}>
                <div style={{
                    position:  "absolute",
                    inset:     0,
                    borderRadius: "50%",
                    background: C.amber,
                    animation: "macroPulse 1.8s ease-in-out infinite",
                }} />
                <style>{`
                    @keyframes macroPulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50%       { opacity: .4; transform: scale(1.5); }
                    }
                `}</style>
            </div>
        </div>
    );
}

// ── Soft caution banner ───────────────────────────────────────────────────────
function SoftCautionBanner({ macro }) {
    const cautionEvent = findCautionEvent(macro.today_high_impact);
    const eventLabel   = cautionEvent
        ? `${cautionEvent.event} at ${cautionEvent.time_et}`
        : "High-impact event approaching";

    return (
        <div style={{
            background:   C.yellow + "08",
            border:       `1px solid ${C.yellow}30`,
            borderLeft:   `3px solid ${C.yellow}60`,
            borderRadius: 8,
            padding:      "10px 20px",
            display:      "flex",
            alignItems:   "center",
            gap:          12,
        }}>
            {/* Info icon */}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="7" cy="7" r="5.5" stroke={C.yellow} strokeWidth="1" opacity=".6"/>
                <line x1="7" y1="6" x2="7" y2="10" stroke={C.yellow} strokeWidth="1.2" strokeLinecap="round" opacity=".7"/>
                <circle cx="7" cy="4.2" r=".7" fill={C.yellow} opacity=".7"/>
            </svg>

            <span style={{
                fontFamily:    FONT,
                fontSize:      9,
                color:         C.yellow + "cc",
                letterSpacing: ".4px",
                lineHeight:    1.6,
            }}>
                Caution: {eventLabel} — bot is in reduced risk mode. Trades may still execute.
            </span>
        </div>
    );
}

// ── Public component ──────────────────────────────────────────────────────────
//
// Props:
//   macro — MacroSummary object from followerMacroEvent
//            { is_paused, pause_type, pause_reason, today_high_impact }

export default function MacroBanner({ macro }) {
    if (!macro) return null;

    if (macro.is_paused && macro.pause_type === "hard_pause") {
        return <HardPauseBanner macro={macro} />;
    }

    if (!macro.is_paused && macro.pause_type === "soft_caution") {
        return <SoftCautionBanner macro={macro} />;
    }

    return null;
}